import { useState } from 'react';

import { Button } from '@/shared/ui/button';
import { SelectField } from '@/shared/ui/select';

import { compareReviews } from '../review-comparison';
import { reviewFailureMessage } from '../review-failure';

import { SubmissionFeedback } from './submission-feedback';
import { SubmissionFiles } from './submission-files';

import type { WorkshopSubmission } from '@mio/shared';

const statusLabels = {
  queued: 'Awaiting review',
  evaluating: 'Reviewing',
  revision_requested: 'Revision suggested',
  passed: 'Complete',
  failed: 'Review unavailable',
};
const resultLabels = {
  met: 'demonstrated',
  partial: 'partly demonstrated',
  not_met: 'not yet demonstrated',
};

export function WorkshopReviewHistory({
  submissions,
  heading = 'Submission history',
  showLatestStatus = true,
}: {
  submissions: WorkshopSubmission[];
  heading?: string;
  showLatestStatus?: boolean;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filesFor, setFilesFor] = useState<string | null>(null);
  const selected = submissions.find((item) => item.id === selectedId) ?? submissions[0];
  if (!selected) return null;
  const index = submissions.indexOf(selected);
  const previous = submissions.slice(index + 1).find((item) => item.evaluation !== null);
  const comparison = previous ? compareReviews(selected, previous) : null;
  const groups = comparison
    ? ([
        ['Improved', comparison.improved],
        ['Needs renewed attention', comparison.regressed],
        ['Still to develop', comparison.unchangedPending],
      ] as const)
    : [];
  return (
    <section aria-label="Submission history" className="mt-6 space-y-4">
      <h3 className="text-fg font-medium">{heading}</h3>
      <p className="text-fg-muted text-xs">
        Your saved submissions. Saved files and explanations remain separate from your current work.
      </p>
      {submissions.length > 1 ? (
        <label className="text-fg block text-sm">
          View submission
          <SelectField
            aria-label="View submission"
            value={selected.id}
            onValueChange={(id) => {
              setSelectedId(id);
              setFilesFor(null);
            }}
            className="mt-2 w-full"
            options={submissions.map((item, itemIndex) => ({
              value: item.id,
              label: `${itemIndex === 0 ? 'Latest · ' : ''}${new Date(item.createdAt).toLocaleString()} · ${statusLabels[item.status]}`,
            }))}
          />
        </label>
      ) : (
        <p className="text-fg-muted text-xs">
          Submitted {new Date(selected.createdAt).toLocaleString()}
        </p>
      )}
      {showLatestStatus || index > 0 ? (
        <p className="text-fg-muted text-sm">{statusLabels[selected.status]}</p>
      ) : null}
      {selected.evaluation ? (
        <SubmissionFeedback
          key={`feedback:${selected.id}`}
          submissionId={selected.id}
          explanation={selected.explanation}
          result={selected.evaluation}
        />
      ) : null}
      {selected.status === 'failed' && (showLatestStatus || index > 0) ? (
        <p className="text-fg-muted text-sm">{reviewFailureMessage(selected.errorCode)}</p>
      ) : null}
      <details className="border-line rounded border p-3">
        <summary className="cursor-pointer text-sm">Submitted explanation</summary>
        <p className="mt-3 whitespace-pre-wrap text-sm">{selected.explanation}</p>
      </details>
      <Button
        variant="secondary"
        onClick={() => setFilesFor(filesFor === selected.id ? null : selected.id)}
        aria-expanded={filesFor === selected.id}
      >
        {filesFor === selected.id ? 'Hide submitted files' : 'View submitted files'}
      </Button>
      {filesFor === selected.id ? (
        <SubmissionFiles key={selected.id} submissionId={selected.id} />
      ) : null}
      {comparison ? (
        <section
          aria-label="Changes since previous review"
          className="text-fg-muted space-y-3 text-sm"
        >
          <h4 className="text-fg font-medium">Changes since the previous review</h4>
          <p>
            Compared using the same rubric and evaluator. These are observations about your
            submissions.
          </p>
          {groups.map(([label, changes]) =>
            changes.length ? (
              <div key={label}>
                <h5 className="text-fg font-medium">{label}</h5>
                <ul className="mt-1 list-disc pl-5">
                  {changes.map((item) => (
                    <li key={item.id}>
                      {item.id.replaceAll('-', ' ')}: {resultLabels[item.before]} →{' '}
                      {resultLabels[item.after]}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null,
          )}
          {!comparison.improved.length && !comparison.regressed.length ? (
            <p>Criterion observations are unchanged.</p>
          ) : null}
        </section>
      ) : selected.evaluation && previous ? (
        <p className="text-fg-muted text-xs">
          These reviews use different or older assessment formats, so no direct comparison is shown.
        </p>
      ) : null}
    </section>
  );
}
